// CoinContext.tsx

import { CoinsResponse, UseCoinBalanceReturn } from "./types";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import AuthContext from "../authContext/AuthContext";
import { AuthContextType } from "../authContext/types";
import { AxiosError } from "axios";

// import config from "../../config";

const CoinContext = createContext<UseCoinBalanceReturn | undefined>(undefined);

export const CoinProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [coins, setCoins] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // apiUrl no longer needed because we use shared api instance

  const { api } = useContext(AuthContext) as AuthContextType;

  const fetchCoins = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get<CoinsResponse>(`/api/coins/`);

      setCoins(response.data.whacky_michal_coins);
    } catch (err) {
      const error = err as Error | AxiosError;
      setError("Failed to load coin balance");
      console.error("Error fetching coins:", error.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const updateCoins = useCallback((newBalance: number): void => {
    setCoins(newBalance);
  }, []);

  const { isAuthenticated } = useContext(AuthContext) as AuthContextType;

  useEffect(() => {
    if (isAuthenticated) {
      fetchCoins();
    }
  }, [isAuthenticated, fetchCoins]);

  const value = useMemo<UseCoinBalanceReturn>(
    () => ({
      coins,
      loading,
      error,
      refreshBalance: fetchCoins,
      updateCoins,
    }),
    [coins, loading, error, fetchCoins, updateCoins]
  );

  return <CoinContext.Provider value={value}>{children}</CoinContext.Provider>;
};

// Custom hook to use the coin context
export const useCoinBalance = (): UseCoinBalanceReturn => {
  const context = useContext(CoinContext);
  if (context === undefined) {
    throw new Error("useCoinBalance must be used within a CoinProvider");
  }
  return context;
};
